import { Observable, OperatorFunction, Subscription } from 'rxjs';

/**
 * Runs at most one inner request at a time and remembers only the latest value that arrived meanwhile.
 * When the running request completes, the remembered value (if any) starts the next one.
 *
 * Unlike switchMap it never cancels a request that already reached the backend (a cancelled HTTP call
 * still runs on the server and occupies a DB session), and unlike exhaustMap it never drops the final
 * state: a burst of N reload triggers produces at most 2 requests.
 */
export function exhaustMapLatest<T, R>(project: (value: T) => Observable<R>): OperatorFunction<T, R> {
  return (source) =>
    new Observable<R>((subscriber) => {
      let inner: Subscription | null = null;
      let queued: { value: T } | null = null;
      let sourceCompleted = false;

      const run = (value: T): void => {
        const subscription = project(value).subscribe({
          next: (result) => subscriber.next(result),
          error: (error) => subscriber.error(error),
          complete: () => {
            inner = null;
            if (queued) {
              const next = queued.value;
              queued = null;
              run(next);
            } else if (sourceCompleted) {
              subscriber.complete();
            }
          },
        });
        // a synchronously completed inner is already closed and must not block the next value
        if (!subscription.closed) {
          inner = subscription;
        }
      };

      const outer = source.subscribe({
        next: (value) => {
          if (inner) {
            queued = { value };
          } else {
            run(value);
          }
        },
        error: (error) => subscriber.error(error),
        complete: () => {
          sourceCompleted = true;
          if (!inner) {
            subscriber.complete();
          }
        },
      });

      return () => {
        outer.unsubscribe();
        inner?.unsubscribe();
      };
    });
}
